// Small building blocks shared by the console screens.
import { useMemo, useState, type ReactNode } from "react";
import { sha256Hex } from "@vavr/core";
import { Banner, Button, Dialog, DialogClose, Spinner, type ButtonProps } from "@vavr/ui";
import { api, fullName } from "../shared/index.ts";
import { useConsole, useGet, useAction } from "./data.tsx";
import { localDate, zonedTime, addDays, type Names } from "./reports.ts";

export function Loading({ label = "Loading" }: { label?: string }) {
  return <div className="pmu-c-loading"><Spinner label={label} /></div>;
}

export function LoadError({ error, retry }: { error: Error; retry?: () => void }) {
  return <Banner tone="danger" title="This could not be loaded" actions={retry && <Button size="sm" variant="secondary" onClick={retry}>Try again</Button>}>{error.message}</Banner>;
}

/** Page toolbar: a heading for the section (the top bar holds the page title) and actions. */
export function Toolbar({ children, actions }: { children?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="pmu-c-toolbar">
      <div className="pmu-c-toolbar__filters">{children}</div>
      {actions && <div className="pmu-c-toolbar__actions">{actions}</div>}
    </div>
  );
}

/** A button that asks first. */
export function ConfirmButton({ title, description, confirmLabel, onConfirm, danger, children, ...button }: ButtonProps & { title: string; description?: ReactNode; confirmLabel: string; danger?: boolean; onConfirm: () => void | Promise<unknown> }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog
      title={title} description={description} open={open} onOpenChange={setOpen}
      trigger={<Button {...button}>{children}</Button>}
      actions={<>
        <DialogClose><Button variant="secondary">Cancel</Button></DialogClose>
        <Button variant={danger || button.variant === "danger" ? "danger" : "primary"} onClick={async () => { await onConfirm(); setOpen(false); }}>{confirmLabel}</Button>
      </>}
    />
  );
}

export const ROLE_LABEL: Record<string, string> = {
  org_admin: "Organization admin", site_admin: "Site admin", door_staff: "Door staff", teacher: "Teacher", auditor: "Auditor", guardian: "Guardian", platform_operator: "Platform operator",
  "transport:dispatcher": "Transport dispatcher", "transport:driver": "Bus driver",
};
export const KIND_LABEL: Record<string, string> = { guardian: "Guardian", authorised: "Authorized", delegate: "Delegate" };
export const RESTRICTION_LABEL: Record<string, string> = { court: "Court order", protective: "Protective order", site: "Site decision" };
export const REQUEST_LABEL: Record<string, string> = { absence: "Absence", early_pickup: "Early pickup", note: "Note" };

// ---------- dates in the site's time zone ----------

/** YYYY-MM-DD for a date input, from epoch ms, in the site's zone. */
export const toDateInput = (ts: number | null | undefined, tz: string) => (ts == null ? "" : localDate(ts, tz));
/** Start of a local day (for "valid from") or its last millisecond (for "valid to"). */
export const fromDateInput = (v: string, tz: string, end = false) => (!v ? null : end ? zonedTime(addDays(v, 1), tz) - 1 : zonedTime(v, tz));
export const today = (tz: string) => localDate(Date.now(), tz);

export function validity(from: number | null, to: number | null, tz: string, now = Date.now()): { label: string; tone: "neutral" | "success" | "warning" } {
  const fmt = (ts: number) => new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: tz }).format(new Date(ts));
  if (to != null && to < now) return { label: `Expired ${fmt(to)}`, tone: "warning" };
  if (from != null && from > now) return { label: `From ${fmt(from)}${to ? ` to ${fmt(to)}` : ""}`, tone: "neutral" };
  if (to != null) return { label: `Until ${fmt(to)}`, tone: "neutral" };
  return { label: "No end date", tone: "success" };
}

// ---------- names ----------

interface Directory { adults: { id: string; first: string; last: string }[]; staff: { id: string; name: string }[]; devices: { id: string; name: string; kind: string }[]; rooms: { id: string; name: string }[] }

/**
 * Names for ids in events and reports. Uses the directory (roles that read every event) and the
 * children list; falls back to short ids when a name is not visible to this role.
 */
export function useNames(): { names: Names; device: (id: string) => string; ready: boolean; children: any[] } {
  const { base, can } = useConsole();
  const dir = useGet<Directory>(can("events.read_all") ? `${base}/directory` : null, 30_000);
  const kids = useGet<{ children: any[] }>(`${base}/children`, 30_000);
  const rooms = useGet<{ rooms: { id: string; name: string }[] }>(`${base}/rooms`, 30_000);
  return useMemo(() => {
    const c = new Map((kids.data?.children ?? []).map((x) => [x.id as string, fullName(x)]));
    const a = new Map((dir.data?.adults ?? []).map((x) => [x.id, fullName(x)]));
    const s = new Map((dir.data?.staff ?? []).map((x) => [x.id, x.name]));
    const d = new Map((dir.data?.devices ?? []).map((x) => [x.id, x.name]));
    const r = new Map((rooms.data?.rooms ?? dir.data?.rooms ?? []).map((x) => [x.id, x.name]));
    const short = (id: string) => `#${id.slice(-6)}`;
    return {
      names: {
        child: (id) => (id ? c.get(id) ?? `Child ${short(id)}` : ""),
        adult: (id) => (id ? a.get(id) ?? `Adult ${short(id)}` : ""),
        staff: (id) => (id ? s.get(id) ?? `Staff ${short(id)}` : ""),
        room: (id) => (id ? r.get(id) ?? "" : ""),
      },
      device: (id: string) => d.get(id) ?? `Device ${short(id)}`,
      ready: !kids.loading && !dir.loading,
      children: kids.data?.children ?? [],
    };
  }, [kids.data, dir.data, rooms.data, kids.loading, dir.loading]);
}

// ---------- uploads ----------

/**
 * Enrolment photos: scaled so the longer side is at most 640 px, re-encoded as JPEG at 0.7
 * (strips EXIF, including location), then uploaded content-addressed. Returns the SHA-256.
 */
export async function uploadPhoto(base: string, file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 640 / Math.max(bitmap.width, bitmap.height));
  const w = Math.max(1, Math.round(bitmap.width * scale)), h = Math.max(1, Math.round(bitmap.height * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d")!.drawImage(bitmap, 0, 0, w, h);
  bitmap.close();
  const blob = await new Promise<Blob>((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error("Could not read that image."))), "image/jpeg", 0.7));
  return uploadBytes(base, new Uint8Array(await blob.arrayBuffer()), "image/jpeg");
}

export async function uploadBytes(base: string, bytes: Uint8Array, type: string): Promise<string> {
  if (bytes.length > 512 * 1024) throw new Error("That file is larger than 512 KB.");
  const sha = await sha256Hex(bytes);
  await api("PUT", `${base}/blobs/${sha}`, undefined, { raw: bytes, type });
  return sha;
}

/** A file picker styled as a button (the native input stays the accessible control). */
export function FileButton({ label, accept, onFile, disabled }: { label: string; accept: string; onFile: (f: File) => void; disabled?: boolean }) {
  return (
    <label className="pmu-c-file">
      <input type="file" accept={accept} disabled={disabled} onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onFile(f); }} />
      <span className="pmu-button pmu-button--secondary">{label}</span>
    </label>
  );
}

export { useAction };
